import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import direction from 'direction';
import { extension, command, PlainExtension, ExtensionTag, hasTransactionChanged, findParentNode, isString, getTextSelection } from '@remirror/core';
import { ExtensionBidiMessages } from '@remirror/messages';

var _dec, _dec2, _class, _class2;
var setTextDirectionOptions = {
  icon: _ref => {
    var attrs = _ref.attrs;
    return (attrs === null || attrs === void 0 ? void 0 : attrs.dir) === 'ltr' ? 'textDirectionL' : 'textDirectionR';
  },
  description: _ref2 => {
    var t = _ref2.t,
        attrs = _ref2.attrs;
    return t(ExtensionBidiMessages.DESCRIPTION, {
      dir: attrs === null || attrs === void 0 ? void 0 : attrs.dir
    });
  },
  label: _ref3 => {
    var t = _ref3.t,
        attrs = _ref3.attrs;
    return t(ExtensionBidiMessages.LABEL, {
      dir: attrs === null || attrs === void 0 ? void 0 : attrs.dir
    });
  }
};
var BidiExtension = (_dec = extension({
  defaultOptions: {
    defaultDirection: undefined,
    autoUpdate: false,
    excludeNodes: []
  },
  staticKeys: ['excludeNodes']
}), _dec2 = command(setTextDirectionOptions), _dec(_class = (_class2 = class BidiExtension extends PlainExtension {
  constructor() {
    super(...arguments);

    _defineProperty(this, "_ignoreNextUpdate", false);
  }

  get name() {
    return 'bidi';
  }
  /**
   * Whether to ignore next updated.
   */


  /**
   * Add the bidi property to the top level editor attributes `doc`.
   */
  createAttributes() {
    if (this.options.defaultDirection) {
      return {
        dir: this.options.defaultDirection
      };
    }

    return {};
  }
  /**
   * Add the `dir` to all the inner node types.
   */


  createSchemaAttributes() {
    var IGNORE_BIDI_AUTO_UPDATE = 'data-ignore-bidi-auto';
    return [{
      identifiers: {
        type: 'node',
        tags: [ExtensionTag.BlockNode],
        excludeNames: this.options.excludeNodes
      },
      attributes: {
        dir: this.dir(),
        ignoreBidiAutoUpdate: {
          default: null,
          parseDOM: IGNORE_BIDI_AUTO_UPDATE,
          toDOM: attrs => attrs.ignoreBidiAutoUpdate ? [IGNORE_BIDI_AUTO_UPDATE, 'true'] : undefined
        }
      }
    }];
  }
  /**
   * Create the plugin that ensures the node has the correct `dir` value on each
   * state update.
   */


  createPlugin() {
    return {
      state: {
        init: () => false,
        apply: tr => {
          if (this._ignoreNextUpdate) {
            this._ignoreNextUpdate = false;
            return false;
          }

          return hasTransactionChanged(tr);
        }
      },
      appendTransaction: (_, __, state) => {
        var shouldUpdate = this.getPluginState();
        var _this$options = this.options,
            autoUpdate = _this$options.autoUpdate,
            excludeNodes = _this$options.excludeNodes;
        var tr = state.tr;

        if (!shouldUpdate || !autoUpdate) {
          return;
        }

        var parent = findParent(state.selection, excludeNodes);

        if (!parent) {
          return;
        }

        var node = parent.node,
            pos = parent.pos;
        var currentDirection = node.attrs.dir;
        var dir = this.getDirection(node.textContent);

        if (currentDirection === dir) {
          return;
        }

        if (node.attrs.ignoreBidiAutoUpdate) {
          return;
        }

        this._ignoreNextUpdate = true;
        return this.store.chain(tr).updateNodeAttributes(pos, _objectSpread(_objectSpread({}, node.attrs), {}, {
          dir
        })).tr();
      }
    };
  }

  onSetOptions(props) {
    var changes = props.changes;

    if (changes.defaultDirection.changed) {
      this.store.updateAttributes();
    }
  }
  /**
   * Create the `SchemaAttributesObject`
   */


  dir() {
    var _this$options$default;

    return {
      default: (_this$options$default = this.options.defaultDirection) !== null && _this$options$default !== void 0 ? _this$options$default : null,
      parseDOM: element => {
        var _element$getAttribute;

        return (_element$getAttribute = element.getAttribute('dir')) !== null && _element$getAttribute !== void 0 ? _element$getAttribute : this.getDirection(element.textContent);
      },
      toDOM: (_, _ref4) => {
        var _this$getDirection;

        var node = _ref4.node;

        if (!node) {
          return;
        }

        if (!node.textContent) {
          return;
        }

        return (_this$getDirection = this.getDirection(node.textContent)) !== null && _this$getDirection !== void 0 ? _this$getDirection : this.options.defaultDirection;
      }
    };
  }
  /**
   * Get the direction of the text.
   */


  getDirection(text) {
    if (!isString(text)) {
      return;
    }

    var dir = direction(text);

    if (dir === 'neutral') {
      return this.options.defaultDirection;
    }

    return dir;
  }

  setTextDirection(dir) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return props => {
      var tr = props.tr;
      var selection = options.selection;
      var cmd = this.store.commands.updateNodeAttributes.original;
      var parent = findParent(getTextSelection(selection !== null && selection !== void 0 ? selection : tr.selection, tr.doc), this.options.excludeNodes); // eslint-disable-next-line eqeqeq

      if (!parent || parent.node.attrs.dir == dir) {
        return false;
      }

      return cmd(parent.pos, {
        dir,
        ignoreBidiAutoUpdate: dir ? true : dir
      })(props);
    };
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "setTextDirection", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "setTextDirection"), _class2.prototype)), _class2)) || _class);

function findParent(selection, excludeNodes) {
  return findParentNode({
    predicate: node => !!(node.isTextblock && node.textContent && !excludeNodes.includes(node.type.name)),
    selection
  });
}

export { BidiExtension };
